import { useId, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { CalendarDays, Check, ChevronRight, Loader2, Plus, Search, UserRound } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { apiClient } from '@/lib/api/client'
import type {
  HelmInquiryPriority,
  HelmUser,
  Inquiry,
  WorkflowTask,
  WorkflowTaskStatus,
} from '@/lib/api/types'
import { kampalaDay } from '@/lib/record-activity'
import { cn } from '@/lib/utils'
import { personName, useHelmPeople } from '@/src/hooks/useHelmPeople'
import { useOptimisticRows } from '@/src/hooks/useOptimisticRows'
import { isTeamMember } from '@/src/lib/roles'

/**
 * An inquiry's tasks, the way Linear lists an issue's sub-issues: each with
 * its status, who does it, and when it is due. Open work first, the task in
 * progress (else the newest to do) leading as the inquiry's next action;
 * done and cancelled tasks fold away under Completed. Staff change a status
 * or an assignee from the row, and add a task at the foot of the list.
 */

const STATUSES: Array<{ status: WorkflowTaskStatus; label: string }> = [
  { status: 'TODO', label: 'Todo' },
  { status: 'IN_PROGRESS', label: 'In Progress' },
  { status: 'DONE', label: 'Done' },
  { status: 'CANCELLED', label: 'Cancelled' },
]

const PRIORITY_LABELS: Record<HelmInquiryPriority, string> = {
  LOW: 'Low',
  NORMAL: 'Normal',
  HIGH: 'High',
  URGENT: 'Urgent',
}

function statusLabel(status: WorkflowTaskStatus) {
  return STATUSES.find((entry) => entry.status === status)?.label ?? status
}

function isOpen(task: Pick<WorkflowTask, 'status'>) {
  return task.status === 'TODO' || task.status === 'IN_PROGRESS'
}

const newestFirst = (first: string | null | undefined, second: string | null | undefined) =>
  new Date(second ?? 0).getTime() - new Date(first ?? 0).getTime()

/**
 * Open tasks as Linear groups them, in progress before to do, each newest
 * first. The first is the one Helm's Workflow card calls the next task.
 */
export function orderOpenTasks<T extends Pick<WorkflowTask, 'status' | 'created_at'>>(tasks: T[]) {
  const rank = (task: T) => (task.status === 'IN_PROGRESS' ? 0 : 1)
  return tasks
    .filter(isOpen)
    .sort(
      (first, second) =>
        rank(first) - rank(second) || newestFirst(first.created_at, second.created_at),
    )
}

/** Linear's status circles, in Helm's status tones. */
export function TaskStatusIcon({
  status,
  className,
}: {
  status: WorkflowTaskStatus
  className?: string
}) {
  const shared = cn('size-3.5 shrink-0', className)
  if (status === 'IN_PROGRESS') {
    return (
      <svg viewBox="0 0 14 14" aria-hidden="true" className={cn(shared, 'text-moving')}>
        <circle cx="7" cy="7" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M7 3.5a3.5 3.5 0 0 1 0 7z" fill="currentColor" />
      </svg>
    )
  }
  if (status === 'DONE') {
    return (
      <svg viewBox="0 0 14 14" aria-hidden="true" className={cn(shared, 'text-done')}>
        <circle cx="7" cy="7" r="7" fill="currentColor" />
        <path
          d="M4.25 7.25l1.9 1.9 3.6-3.9"
          fill="none"
          stroke="var(--background)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  if (status === 'CANCELLED') {
    return (
      <svg viewBox="0 0 14 14" aria-hidden="true" className={cn(shared, 'text-muted-foreground')}>
        <circle cx="7" cy="7" r="7" fill="currentColor" />
        <path
          d="M4.9 4.9l4.2 4.2M9.1 4.9l-4.2 4.2"
          fill="none"
          stroke="var(--background)"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 14 14" aria-hidden="true" className={cn(shared, 'text-muted-foreground')}>
      <circle cx="7" cy="7" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}

/** Linear's priority bars: one to three for low to high, a mark for urgent. */
function PriorityIcon({ priority }: { priority: HelmInquiryPriority }) {
  const label = `Priority: ${PRIORITY_LABELS[priority]}`
  if (priority === 'URGENT') {
    return (
      <svg
        viewBox="0 0 14 14"
        role="img"
        aria-label={label}
        className="size-3.5 shrink-0 text-waiting"
      >
        <title>{label}</title>
        <rect x="1" y="1" width="12" height="12" rx="3" fill="currentColor" />
        <path d="M7 4v3.5" stroke="var(--background)" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="7" cy="10" r="0.9" fill="var(--background)" />
      </svg>
    )
  }
  const filled = priority === 'LOW' ? 1 : priority === 'NORMAL' ? 2 : 3
  return (
    <svg
      viewBox="0 0 14 14"
      role="img"
      aria-label={label}
      className="size-3.5 shrink-0 text-muted-foreground"
    >
      <title>{label}</title>
      {[0, 1, 2].map((bar) => (
        <rect
          key={bar}
          x={1.5 + bar * 4}
          y={9 - bar * 3.5}
          width="3"
          height={3.5 + bar * 3.5}
          rx="1"
          fill="currentColor"
          opacity={bar < filled ? 1 : 0.3}
        />
      ))}
    </svg>
  )
}

function initials(name: string) {
  const words = name
    .replace(/@.*/, '')
    .split(/[\s._-]+/)
    .filter(Boolean)
  return (
    words.length > 1 ? `${words[0]![0]}${words[1]![0]}` : (words[0] ?? '?').slice(0, 2)
  ).toUpperCase()
}

function PersonAvatar({ user, name }: { user?: HelmUser; name: string }) {
  return (
    <Avatar className="size-5">
      {user?.image && <AvatarImage src={user.image} alt="" />}
      <AvatarFallback className="bg-secondary text-[0.625rem] font-semibold text-secondary-foreground">
        {initials(name)}
      </AvatarFallback>
    </Avatar>
  )
}

/** Nobody yet: Linear's dashed person. */
function NobodyAvatar() {
  return (
    <span className="flex size-5 shrink-0 items-center justify-center rounded-full border border-dashed border-muted-foreground/60 text-muted-foreground">
      <UserRound className="size-3" aria-hidden="true" />
    </span>
  )
}

function dueTone(task: Pick<WorkflowTask, 'due_at' | 'status'>, now: number) {
  if (!task.due_at || !isOpen(task)) return 'text-muted-foreground'
  return new Date(task.due_at).getTime() < now ? 'text-blocked' : 'text-muted-foreground'
}

interface People {
  users: Map<string, HelmUser>
}

function nameOf(people: People, userId: string | null | undefined) {
  if (!userId) return null
  const user = people.users.get(userId)
  return user ? personName(user) : 'Someone'
}

/** Who does a task: a menu of the team, searchable, with "No assignee" first. */
function AssigneePicker({
  value,
  people,
  team,
  disabled,
  onSelect,
  taskTitle,
}: {
  value: string | null
  people: People
  team: HelmUser[]
  disabled?: boolean
  onSelect: (userId: string | null) => void
  /** Names the task in the button's label; none for a task being written. */
  taskTitle?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const name = nameOf(people, value)
  const user = value ? people.users.get(value) : undefined
  const matches = useMemo(() => {
    const wanted = query.trim().toLowerCase()
    if (!wanted) return team
    return team.filter((member) =>
      [member.display_name, member.name, member.email].some((field) =>
        String(field ?? '')
          .toLowerCase()
          .includes(wanted),
      ),
    )
  }, [query, team])

  function choose(userId: string | null) {
    setOpen(false)
    setQuery('')
    if (userId !== value) onSelect(userId)
  }

  const label = `Assignee: ${name ?? 'nobody'}${taskTitle ? `. Change who does "${taskTitle}"` : ''}`
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setQuery('')
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={label}
          title={user?.email ?? name ?? 'No assignee'}
          className="flex min-w-0 max-w-40 items-center gap-1.5 rounded-full py-0.5 pr-2 pl-0.5 text-xs text-muted-foreground transition-colors outline-none hover:bg-accent hover:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-60"
        >
          {name ? <PersonAvatar user={user} name={name} /> : <NobodyAvatar />}
          <span className="min-w-0 truncate">{name ?? 'No assignee'}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="size-4 text-muted-foreground" aria-hidden="true" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Assign to…"
            aria-label="Find a member of staff"
            className="h-8 border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>
        <div role="listbox" aria-label="Assignee" className="max-h-72 overflow-y-auto p-1">
          {!query.trim() && (
            <AssigneeOption selected={!value} onSelect={() => choose(null)}>
              <NobodyAvatar />
              <span className="truncate">No assignee</span>
            </AssigneeOption>
          )}
          {matches.map((member) => (
            <AssigneeOption
              key={member.id}
              selected={member.id === value}
              onSelect={() => choose(member.id)}
            >
              <PersonAvatar user={member} name={personName(member)} />
              <span className="min-w-0 flex-1 truncate">{personName(member)}</span>
            </AssigneeOption>
          ))}
          {matches.length === 0 && (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">
              {team.length ? 'Nobody on the team matches that.' : 'The team could not be loaded.'}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function AssigneeOption({
  selected,
  onSelect,
  children,
}: {
  selected: boolean
  onSelect: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent"
    >
      {children}
      <Check className={cn('ml-auto size-4', selected ? 'opacity-100' : 'opacity-0')} />
    </button>
  )
}

/** A task's status, changed from its circle as in Linear. */
function StatusMenu({
  task,
  disabled,
  onSelect,
}: {
  task: Pick<WorkflowTask, 'status' | 'title'>
  disabled?: boolean
  onSelect: (status: WorkflowTaskStatus) => void
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={`Status: ${statusLabel(task.status)}. Change the status of "${task.title}"`}
          title={statusLabel(task.status)}
          className="-m-1.5 flex size-7 shrink-0 items-center justify-center rounded-full outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-60"
        >
          <TaskStatusIcon status={task.status} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-44">
        {STATUSES.map((entry) => (
          <DropdownMenuItem
            key={entry.status}
            onSelect={() => {
              if (entry.status !== task.status) onSelect(entry.status)
            }}
          >
            <TaskStatusIcon status={entry.status} />
            {entry.label}
            {entry.status === task.status && <Check className="ml-auto" aria-label="Current" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function TaskRow({
  task,
  next,
  canEdit,
  saving,
  people,
  team,
  now,
  onStatus,
  onAssignee,
}: {
  task: WorkflowTask
  next: boolean
  canEdit: boolean
  saving: boolean
  people: People
  team: HelmUser[]
  now: number
  onStatus: (status: WorkflowTaskStatus) => void
  onAssignee: (userId: string | null) => void
}) {
  const assignee = nameOf(people, task.assignee_user_id)
  return (
    <li
      data-task-id={task.id}
      aria-label={task.title}
      className="flex items-start gap-2.5 px-3 py-2.5 transition-colors hover:bg-muted/40 sm:items-center"
    >
      <span className="mt-[3px] flex sm:mt-0">
        {canEdit ? (
          <StatusMenu task={task} disabled={saving} onSelect={onStatus} />
        ) : (
          <span title={statusLabel(task.status)}>
            <TaskStatusIcon status={task.status} />
            <span className="sr-only">{statusLabel(task.status)}</span>
          </span>
        )}
      </span>
      <div className="min-w-0 flex-1 sm:flex sm:items-center sm:gap-3">
        <p
          className={cn(
            'text-sm leading-5 wrap-anywhere sm:min-w-0 sm:flex-1',
            !isOpen(task) && 'text-muted-foreground',
          )}
        >
          {task.title}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 sm:mt-0 sm:shrink-0 sm:flex-nowrap">
          {next && (
            <Badge variant="secondary" title="The inquiry's next action">
              Next
            </Badge>
          )}
          {saving && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
          <PriorityIcon priority={task.priority} />
          {task.due_at && (
            <span
              className={cn('flex items-center gap-1 text-xs tabular-nums', dueTone(task, now))}
              title={`Due ${new Date(task.due_at).toLocaleString('en-GB', { timeZone: 'Africa/Kampala' })}`}
            >
              <CalendarDays className="size-3.5" aria-hidden="true" />
              <span className="sr-only">Due</span>
              {kampalaDay(task.due_at)}
            </span>
          )}
          {canEdit ? (
            <AssigneePicker
              value={task.assignee_user_id}
              people={people}
              team={team}
              disabled={saving}
              onSelect={onAssignee}
              taskTitle={task.title}
            />
          ) : (
            <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
              {assignee ? (
                <PersonAvatar
                  user={people.users.get(task.assignee_user_id ?? '')}
                  name={assignee}
                />
              ) : (
                <NobodyAvatar />
              )}
              <span className="truncate">{assignee ?? 'No assignee'}</span>
            </span>
          )}
        </div>
      </div>
    </li>
  )
}

/** A new task, written at the foot of the open list as in Linear. */
function NewTaskForm({
  inquiryId,
  defaultAssignee,
  people,
  team,
  onAdded,
  onClose,
}: {
  inquiryId: string
  defaultAssignee: string | null
  people: People
  team: HelmUser[]
  onAdded: (task: WorkflowTask) => void
  onClose: () => void
}) {
  const id = useId()
  const [title, setTitle] = useState('')
  const [assignee, setAssignee] = useState<string | null>(defaultAssignee)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    const trimmed = title.trim()
    if (!trimmed) {
      setError('Say what needs doing.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const task = await apiClient.createInquiryTask(inquiryId, {
        title: trimmed,
        assignee_user_id: assignee,
      })
      setTitle('')
      onAdded(task)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The task was not added.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <li className="px-3 py-2.5">
      <form
        onSubmit={submit}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !saving) onClose()
        }}
        className="flex flex-col gap-2 sm:flex-row sm:items-center"
        aria-label="New task"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <TaskStatusIcon status="TODO" />
          <label htmlFor={`${id}-title`} className="sr-only">
            Task title
          </label>
          <Input
            id={`${id}-title`}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="What needs doing?"
            maxLength={500}
            autoFocus
            disabled={saving}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${id}-error` : undefined}
            className="h-8"
          />
        </div>
        <div className="flex items-center justify-end gap-1.5">
          <AssigneePicker
            value={assignee}
            people={people}
            team={team}
            disabled={saving}
            onSelect={setAssignee}
          />
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={saving}>
            {saving && <Loader2 className="animate-spin" />}
            Add task
          </Button>
        </div>
      </form>
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1.5 text-xs text-destructive">
          {error}
        </p>
      )}
    </li>
  )
}

export function InquiryTasks({
  inquiry,
  canEdit,
  onChanged,
}: {
  inquiry: Pick<Inquiry, 'id' | 'owner_user_id' | 'tasks'>
  /** Staff who may change tasks; others only read them. */
  canEdit: boolean
  /** Reloads the inquiry once Helm has a change, behind what is on screen. */
  onChanged: () => void
}) {
  const people = useHelmPeople()
  const directory: People = useMemo(
    () => ({ users: people.data?.users ?? new Map<string, HelmUser>() }),
    [people.data],
  )
  const team = useMemo(
    () =>
      [...directory.users.values()]
        .filter(isTeamMember)
        .sort((first, second) => personName(first).localeCompare(personName(second))),
    [directory],
  )
  // Added here and not yet back from the reload: shown at once all the same.
  const [added, setAdded] = useState<WorkflowTask[]>([])
  const tasks = useMemo(
    () => [
      ...inquiry.tasks,
      ...added.filter((task) => !inquiry.tasks.some((row) => row.id === task.id)),
    ],
    [inquiry.tasks, added],
  )
  const edits = useOptimisticRows(tasks)
  const [adding, setAdding] = useState(false)
  const [showCompleted, setShowCompleted] = useState(false)
  // A due date is overdue as of when the page drew it.
  const [now] = useState(() => Date.now())

  const open = orderOpenTasks(edits.rows)
  const completed = edits.rows
    .filter((task) => !isOpen(task))
    .sort((first, second) =>
      newestFirst(first.completed_at ?? first.updated_at, second.completed_at ?? second.updated_at),
    )
  const nextId = open[0]?.id ?? null
  const completedId = useId()

  async function change(
    task: WorkflowTask,
    patch: Pick<WorkflowTask, 'status'> | Pick<WorkflowTask, 'assignee_user_id'>,
  ) {
    await edits.save(
      task,
      patch,
      () =>
        apiClient.updateInquiryTask(inquiry.id, task.id, {
          ...patch,
          expected_updated_at: task.updated_at,
        }),
      `"${task.title}"`,
    )
    // Saved or refused, the page catches up with Helm: after a refusal for a
    // stale copy, the task shows as it now is.
    onChanged()
  }

  const doneCount = completed.filter((task) => task.status === 'DONE').length
  const defaultAssignee =
    inquiry.owner_user_id && team.some((member) => member.id === inquiry.owner_user_id)
      ? inquiry.owner_user_id
      : null

  return (
    <Card data-tour="inquiry-tasks" role="region" aria-labelledby="inquiry-tasks-title">
      <CardHeader className="pb-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle id="inquiry-tasks-title" className="flex items-center gap-2">
              Tasks
              {tasks.length > 0 && (
                <span
                  className="text-sm font-normal text-muted-foreground tabular-nums"
                  aria-label={`${doneCount} of ${tasks.length} done`}
                >
                  {doneCount}/{tasks.length}
                </span>
              )}
            </CardTitle>
            <CardDescription className="mt-1.5">
              What the team has to do on this inquiry, and who is doing it.
            </CardDescription>
          </div>
          {canEdit && !adding && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(true)}>
              <Plus />
              Add task
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {edits.failure && (
          <Alert variant="destructive">
            <AlertDescription className="flex items-start justify-between gap-3">
              <span>{edits.failure}</span>
              <button
                type="button"
                className="shrink-0 text-xs underline underline-offset-2"
                onClick={edits.dismissFailure}
              >
                Dismiss
              </button>
            </AlertDescription>
          </Alert>
        )}
        {open.length === 0 && !adding ? (
          <p className="rounded-lg border border-dashed px-3 py-4 text-sm text-muted-foreground">
            {completed.length ? 'Every task is done.' : 'No tasks yet.'}
          </p>
        ) : (
          <ul aria-label="Open tasks" className="divide-y rounded-lg border">
            {open.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                next={task.id === nextId}
                canEdit={canEdit}
                saving={edits.saving.has(task.id)}
                people={directory}
                team={team}
                now={now}
                onStatus={(status) => void change(task, { status })}
                onAssignee={(assignee_user_id) => void change(task, { assignee_user_id })}
              />
            ))}
            {adding && (
              <NewTaskForm
                inquiryId={inquiry.id}
                defaultAssignee={defaultAssignee}
                people={directory}
                team={team}
                onAdded={(task) => {
                  setAdded((current) => [...current, task])
                  setAdding(false)
                  onChanged()
                }}
                onClose={() => setAdding(false)}
              />
            )}
          </ul>
        )}
        {completed.length > 0 && (
          <div>
            <button
              type="button"
              aria-expanded={showCompleted}
              aria-controls={completedId}
              onClick={() => setShowCompleted((current) => !current)}
              className="flex items-center gap-1.5 rounded-md px-1 py-1 text-sm font-medium text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <ChevronRight
                className={cn('size-4 transition-transform', showCompleted && 'rotate-90')}
                aria-hidden="true"
              />
              Completed
              <span className="tabular-nums">{completed.length}</span>
            </button>
            {showCompleted && (
              <ul
                id={completedId}
                aria-label="Completed tasks"
                className="mt-2 divide-y rounded-lg border"
              >
                {completed.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    next={false}
                    canEdit={canEdit}
                    saving={edits.saving.has(task.id)}
                    people={directory}
                    team={team}
                    now={now}
                    onStatus={(status) => void change(task, { status })}
                    onAssignee={(assignee_user_id) => void change(task, { assignee_user_id })}
                  />
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
